import { speedUnitBracket, speedUnitBracketUpper, type SpeedDisplayUnit } from '../../../utils/speedUnits';
import {
  GP50_MANEUVER_COLUMN_HEADERS,
  GP50_MANEUVER_METRIC_COLUMN_KEYS,
} from '../../../utils/gp50ManeuverCheatSheetColumns';
import { ROUNDUP_BEARAWAY_SUMMARY_METRIC_CHANNELS } from '../../../utils/maneuversConfig';

/** Config Cheat Sheet report page title and intro copy (fleet + source pages). */
export const CONFIG_CHEAT_SHEET_PAGE_TITLE = 'CONFIG CHEAT SHEET';
export const CONFIG_CHEAT_SHEET_PAGE_DESCRIPTION =
  'The intention of this page is to highlight differences between configurations with a focus on higher performing data and stricter filtering criteria.';

// =============================================================================
// Cheat sheet table headers — edit the two maps below (straight-line, then maneuvers).
//
// Straight-line: all keys here are fixed strings. BSP and VMG speed columns are merged
// in `cheatSheetDatasetChannelHeaders` using the user's speed unit (KTS/KPH/…).
//
// Maneuvers: `cheatSheetManeuverWindMetricOrder` is a stable column order reference for maneuver metrics.
// =============================================================================

/** Straight-line cheat sheet (events_aggregate / BIN 10). Keys = lowercase row keys. */
export const cheatSheetStraightLineColumnLabels: Record<string, string> = {
  tws: 'TWS',
  config: 'CONFIG',
  twa_n: 'CWA [DEG]',
  twa: 'CWA [DEG]',
  vmg_percent: 'VMG%',
  heel_n: 'HEEL_N [DEG]',
  trim: 'TRIM [DEG]',
  pitch: 'TRIM [DEG]',
  lwy_n: 'LWY_n [DEG]',
  hullsinkmin: 'HULL SINK [M]',
  rh_lwd: 'HULL SINK [M]',
  db_piercing_lwd: 'DB PIERCING [MM]',
  foillwdjunctionsink: 'WING SINK [M]',
  rudderrakeang: 'RUD RAKE [DEG]',
  rud_rake: 'RUD RAKE [DEG]',
  rudderbladesink: 'RUD DRAFT [M]',
  foillwdcantang: 'LWD CANT [DEG]',
  db_cant: 'LWD CANT [DEG]',
  foillwdcantang_eff: 'LWD CANT EFF [DEG]',
  db_cant_eff: 'LWD CANT EFF [DEG]',
  foilwwdcantang: 'WWD CANT [DEG]',
  db_cant_stow: 'WWD CANT [DEG]',
  botang_n: 'BOT ANG [DEG]',
  wing_ca1: 'BOT ANG [DEG]',
  mainsheetload: 'MAINSHEET [KGF]',
  travelerang_n: 'TRAV ANG [DEG]',
  travelerangle_n: 'TRAV ANG [DEG]',
  spannerang_n: 'MAST ROT [DEG]',
  wing_twist: 'MAST ROT [DEG]',
  jibsheetload: 'JIB SHT [KGF]',
  jib_sht: 'JIB SHT [KGF]',
  jibcunnload: 'JIB CUNNO [KGF]',
  jib_cunno: 'JIB CUNNO [KGF]',
  wing_clew: 'WING CLEW [M]',
  outhaul_clew_pct: 'OUTHAUL CLEW [%]',
  mainouthaulposavg_perc: 'OUTHAUL CLEW [%]',
  jib_lead: 'JIB LEAD [DEG]',
  count: 'COUNT',
};

/**
 * Straight-line cheat sheet column order (row keys are lowercased after fetch).
 * Hull / foil / rudder / cant / mainsail / jib — matches product preference; unknown API keys append after this list.
 */
export const cheatSheetStraightLineTableColumnOrder: readonly string[] = [
  'bsp',
  'twa_n',
  'vmg',
  'vmg_percent',
  'heel_n',
  'trim',
  'lwy_n',
  'hullsinkmin',
  'foillwdjunctionsink',
  'rudderrakeang',
  'rudderbladesink',
  'foillwdcantang',
  'foillwdcantang_eff',
  'foilwwdcantang',
  'botang_n',
  'mainsheetload',
  'travelerang_n',
  'spannerang_n',
  'jibsheetload',
  'jibcunnload',
  'outhaul_clew_pct',
];

/** Maneuver cheat sheet (maneuver_stats). Keys = lowercase row / API keys. */
export const cheatSheetManeuverColumnLabels: Record<string, string> = {
  tws: 'TWS',
  config: 'CONFIG',
  drop_time: 'Drop time [s]',
  bsp_drop: 'BSP DROP [KPH]',
  bsp_entry: 'BSP ENTRY [KPH]',
  bsp_max: 'MAX BSP [KPH]',
  bsp_min: 'BS MIN [KPH]',
  twa_drop: 'Twa drop [deg]',
  cant_drop_tgt: 'Drop cant tgt [deg]',
  sink_wwd_drop: 'Sink wwd drop [mm]',
  sink_lwd_drop: 'Sink lwd drop [mm]',
  pitch_drop: 'Trim drop [deg]',
  turn_rate_max: 'Turn rate max [deg/s]',
  twa_entry: 'Twa entry [deg]',
  twa_exit: 'Twa exit [deg]',
  overshoot_angle: 'os angle [deg]',
  raise_time: 'Raise time [s]',
  time_two_boards: 'Time two boards [s]',
  pitch_accmax: 'Trim AccMax [deg]',
  heel_accmax: 'Heel accmax [deg]',
  cant_accmax: 'DB CANT ACCMAX [deg]',
  cant_eff_accmax: 'Cant eff accmax [deg]',
  mainsheet_accmax: 'Mainsheet accmax [KGF]',
  traveler_angle_accmax: 'TRAV ANG ACCMAX [deg]',
  jib_sheet_load_accmax: 'Jib sheet load accmax [KGF]',
  loss_inv_tgt: 'Loss inv tgt',
  loss_turn_tgt: 'Loss turn tgt',
  loss_build_tgt: 'Loss build tgt',
  loss_total_tgt: 'Loss total tgt',
  mmg: 'MMG [M]',
  bsp_min_delta: 'BS MIN Δ [KPH]',
  turn_radius: 'TURN RADIUS [M]',
  // Legacy / alternate keys (older rows or API)
  wing_twist_accmax: 'WING TWIST ACCMAX [deg]',
  wing_ca1_accmax: 'WING CA1 ACCMAX [deg]',
  jib_lead_ang_accmax: 'JIB LEAD ACCMAX [deg]',
  wing_clew_pos_accmax: 'Bot ang accmax [deg]',
  jib_sheet_pct_accmax: 'Jib sheet load accmax [KGF]',
  count: 'COUNT',
};

/** GP50 tack/gybe metric order — from gp50ManeuverCheatSheetColumns (not AC40 sink/drop fields). */
export const cheatSheetManeuverWindMetricOrder: string[] = [...GP50_MANEUVER_METRIC_COLUMN_KEYS];

/** Roundup/bearaway — re-exported from maneuversConfig (getBigTableColumns metric order). */
export { ROUNDUP_BEARAWAY_SUMMARY_METRIC_CHANNELS as cheatSheetManeuverRoundupBearawayMetricOrder } from '../../../utils/maneuversConfig';

export function isRoundupBearawayManeuverType(maneuverType: string): boolean {
  const t = String(maneuverType ?? '').trim().toLowerCase();
  return t === 'roundup' || t === 'bearaway';
}

export function cheatSheetManeuverMetricOrderForType(maneuverType: string): string[] {
  return isRoundupBearawayManeuverType(maneuverType)
    ? [...ROUNDUP_BEARAWAY_SUMMARY_METRIC_CHANNELS]
    : cheatSheetManeuverWindMetricOrder;
}

/** Channel-group column headers for dataset cheat sheet (adds BSP/VMG with speed unit). */
export function cheatSheetDatasetChannelHeaders(unit: SpeedDisplayUnit): Record<string, string> {
  const U = speedUnitBracketUpper(unit);
  return {
    ...cheatSheetStraightLineColumnLabels,
    bsp: `BSP ${U}`,
    vmg: `VMG ${U}`,
  };
}

export function cheatSheetWindColumnsHint(unit: SpeedDisplayUnit): string {
  return ` Columns are bins of TWS ${speedUnitBracket(unit)}.`;
}

/** Header for the TWS bin row key (matches speed display preference). */
export function cheatSheetTwsBinColumnHeader(unit: SpeedDisplayUnit): string {
  const U = speedUnitBracketUpper(unit);
  return `TWS ${U}`;
}

/** Explanatory sentence: one row per TWS bin; columns = averaged metrics (all setups pooled). */
export function cheatSheetTwsByConfigTableHint(unit: SpeedDisplayUnit): string {
  const U = speedUnitBracketUpper(unit);
  return ` Each row is one TWS bin (${U}); remaining columns are mean values of each metric after filtering (all CONFIGs combined).`;
}

export function cheatSheetManeuverWindMetrics(): { label: string; value: string }[] {
  return cheatSheetManeuverWindMetricOrder.map((value) => ({
    value,
    label: cheatSheetManeuverColumnLabels[value] ?? value,
  }));
}

export function cheatSheetManeuverTableHeader(
  key: string,
  unit?: SpeedDisplayUnit,
  maneuverType?: string
): string {
  const k = key.toLowerCase();
  if (k === 'tws') { return unit != null ? cheatSheetTwsBinColumnHeader(unit) : 'TWS'; }
  if (GP50_MANEUVER_COLUMN_HEADERS[k]) { return GP50_MANEUVER_COLUMN_HEADERS[k]; }
  const base = cheatSheetManeuverColumnLabels[k] ?? key;
  if (String(maneuverType ?? '').trim().toLowerCase() === 'roundup' && /accmax/i.test(base)) {
    return base.replace(/accmax/gi, 'TURN');
  }
  return base;
}

/** Short purpose line beside cheat sheet section titles. */
export const cheatSheetStraightLinePurpose =
  'Best straight line data through the range of conditions';

/** Short purpose line beside cheat sheet maneuvers section title. */
export const cheatSheetManeuversPurpose =
  'Statistics from the best maneuvers through a range of conditions';

/** Straight-line cheat sheet table filter explanation (before TWS-by-config hint). */
export function cheatSheetStraightLineFilterExplanation(rankLabel: string): string {
  return (
    `Only BIN 10 segments with grade > 1 (${rankLabel} between 95% and 135%, grade 3 weighted 3× vs grade 2) are included. ` +
    `Outliers are removed using IQR (values between the 25th and 75th percentiles). From that cleaned set we take the upper 10% by ${rankLabel} within each TWS bin, then show the weighted mean of each metric for that bin (all setups pooled).`
  );
}

/** Maneuver cheat sheet table filter explanation (before TWS-by-config hint). */
export function cheatSheetManeuverFilterExplanation(): string {
  return (
    'Only maneuvers with grade > 1 (grade 3 weighted 3× vs grade 2) are included. ' +
    'From the 25th and 75th percentiles of Loss_total_tgt we take the lowest 10% of loss within each TWS bin, then show the weighted mean of each metric for that bin (all setups pooled).'
  );
}

/** Dataset-view / Summary Tables straight-line methodology (user filters listed separately). */
export function dailyTrainingStraightLineFilterExplanation(
  _legType: 'upwind' | 'downwind' | 'reaching' = 'upwind'
): string {
  return (
    'Only BIN 10 straight-line segments with grade > 1 are included. ' +
    'Columns show the mean of each metric across all matching segments.'
  );
}

/** Daily training settings maneuver methodology (user filters listed separately). */
export function dailyTrainingManeuverFilterExplanation(): string {
  return (
    'Columns show the mean of each maneuver metric across all matching maneuvers.'
  );
}
